/**
 * Shared react-query client factory for MV2 nav modules.
 */
import { QueryClient } from "@tanstack/react-query";

/**
 * How many attempts a transient failure gets after the first.
 *
 * With the delay below, a request that keeps failing gives up after roughly
 * seven seconds — long enough to ride out a dropped connection, short enough
 * that a genuinely dead endpoint still surfaces an error promptly.
 */
const MAX_TRANSIENT_RETRIES = 3;

/** 4xx codes that do change on their own: a timeout and a rate limit. */
const RETRYABLE_4XX = new Set([408, 429]);

/**
 * Whether an MV2 query is worth retrying.
 *
 * The distinction that matters is whether the request got an answer at all.
 * With no response it never completed — a flaky connection, a backgrounded
 * tab, a dropped socket — and trying again is usually all it takes. That is
 * the shape behind the dashboard's "could not load": the backend logged
 * nothing for those attempts, because nothing arrived.
 *
 * A 4xx did get an answer, and the answer will not change on a retry. 401
 * especially: the axios interceptor expires the manager session and redirects
 * to login, so retrying only delays that. The two exceptions above are the
 * codes that explicitly mean "later".
 *
 * A 5xx is worth another go — the backend may be mid-restart.
 */
export function shouldRetryMv2Query(failureCount, error) {
  if (failureCount >= MAX_TRANSIENT_RETRIES) return false;
  const status = error?.response?.status;
  if (status === undefined) return true;
  if (status >= 400 && status < 500) return RETRYABLE_4XX.has(status);
  return true;
}

/** 1s, 2s, 4s. Capped well below react-query's 30s default: a stat tile that
 *  takes half a minute to admit failure has already lost the reader. */
export function mv2RetryDelay(attemptIndex) {
  return Math.min(1000 * 2 ** attemptIndex, 8000);
}

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 5_000,
  gcTime: 30 * 60 * 1000,
  retry: shouldRetryMv2Query,
  retryDelay: mv2RetryDelay,
  refetchOnWindowFocus: true,
};

export function makeMv2QueryClient(options = {}) {
  const { defaultOptions: userDefaults, ...rest } = options;
  return new QueryClient({
    ...rest,
    defaultOptions: {
      ...userDefaults,
      queries: {
        ...DEFAULT_QUERY_OPTIONS,
        ...userDefaults?.queries,
      },
    },
  });
}
